import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Loader2, Newspaper } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, get, errMsg } from "@/lib/api";
import { fromLocalInput, toLocalInput } from "@/lib/format";
import { FieldLabel, Panel, StatusBadge, TagInput, ORIGIN } from "@/admin/ui";
import { RichEditor } from "@/admin/RichEditor";
import { ImagePanel, SeoPanel, KeyPointsPanel, FaqPanel } from "@/admin/EditorPanels";
import { AdvancedSeoPanel } from "@/admin/AdvancedSeoPanel";
import { useAdminCategories, useRefresh } from "@/admin/hooks";

const EMPTY = { title: "", summary: "", body: "", category_slug: "", tags: [], key_points: [], faqs: [], image_url: null, image_alt: "", image_credit: "",
  status: "published", publish_at: null, featured: false, seo_title: "", seo_description: "", slug: "", author: "", focus_keyword: "",
  canonical_url: "", noindex: false, schema_type: null, social_title: "", social_description: "", social_image_url: null };

const AutoTextarea = ({ className, ...props }) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) { ref.current.style.height = "auto"; ref.current.style.height = ref.current.scrollHeight + "px"; } }, [props.value]);
  return <textarea ref={ref} rows={1} className={`w-full resize-none bg-transparent outline-none placeholder:text-muted-foreground/50 ${className}`} {...props} />;
};

const PublishPanel = ({ form, patch }) => (
  <Panel title="Publicación" testid="editor-publish-panel">
    <div className="grid grid-cols-2 gap-1 p-1 bg-muted">
      {[["published", "Publicar ahora"], ["scheduled", "Programar"]].map(([k, label]) => (
        <button key={k} type="button" onClick={() => patch({ status: k })} data-testid={`editor-when-${k}`}
          className={`py-2 text-sm transition-colors ${form.status === k ? "bg-card shadow-sm font-semibold" : "text-muted-foreground"}`}>{label}</button>
      ))}
    </div>
    {form.status === "scheduled" && (
      <div className="mt-3">
        <FieldLabel>Fecha y hora de publicación</FieldLabel>
        <input type="datetime-local" value={toLocalInput(form.publish_at)} onChange={(e) => patch({ publish_at: fromLocalInput(e.target.value) })}
          className="w-full border border-input bg-background px-3 h-9 text-sm" data-testid="editor-publish-at-input" />
      </div>
    )}
    <label className="mt-4 flex items-center justify-between gap-3 text-sm">Destacar en portada<Switch checked={form.featured} onCheckedChange={(v) => patch({ featured: v })} data-testid="editor-featured-switch" /></label>
  </Panel>
);

export default function ArticleEditor() {
  const { id } = useParams();
  const isNew = id === "nueva";
  const navigate = useNavigate();
  const refresh = useRefresh();
  const { data: cats } = useAdminCategories();
  const { data, isLoading } = useQuery({ queryKey: ["admin-article", id], queryFn: () => get(`/admin/articles/${id}`), enabled: !isNew });
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(null);
  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  useEffect(() => {
    if (isNew) { setForm(EMPTY); setSaved(null); }
    else if (data) { setForm({ ...EMPTY, ...data, status: data.status === "draft" ? "published" : data.status }); setSaved(data); }
  }, [data, isNew]);
  useEffect(() => { if (!form.category_slug && cats?.length) patch({ category_slug: cats[0].slug }); }, [cats, form.category_slug]);

  const save = useMutation({
    mutationFn: (requested) => {
      let status = requested;
      if (status === "scheduled") {
        if (!form.publish_at) throw new Error("Elige la fecha y hora de publicación");
        if (new Date(form.publish_at) <= new Date()) {
          toast.info("Esa fecha ya ha pasado: la noticia se publica ahora");
          status = "published";
        }
      }
      const body = { ...form, status };
      return (isNew ? api.post("/admin/articles", body) : api.put(`/admin/articles/${id}`, body)).then((r) => r.data);
    },
    onSuccess: (doc) => {
      toast.success({ published: "Noticia publicada", scheduled: "Noticia programada", draft: "Borrador guardado" }[doc.status]);
      refresh();
      setSaved(doc);
      setForm((f) => ({ ...f, slug: doc.slug, body: doc.body }));
      if (isNew) navigate(`/admin/noticias/${doc.id}`, { replace: true });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!isNew && isLoading) return <div className="py-20 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const primaryLabel = form.status === "scheduled" ? "Programar" : saved?.status === "published" ? "Actualizar" : "Publicar";

  return (
    <div data-testid="article-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Link to="/admin/noticias" className="h-9 w-9 grid place-items-center border border-border bg-card hover:border-foreground" aria-label="Volver" data-testid="editor-back-link"><ArrowLeft className="h-4 w-4" /></Link>
          <div>
            <p className="kicker text-primary text-[10px]">{isNew ? "Nueva noticia" : ORIGIN[saved?.origin] || "Editar noticia"}</p>
            <div className="flex items-center gap-2 mt-0.5"><h1 className="font-display font-bold text-xl">{isNew ? "Escribir noticia" : "Editar noticia"}</h1>{saved && <StatusBadge status={saved.status} testid="editor-status-badge" />}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {saved?.status === "published" && <Button variant="ghost" asChild><a href={`/noticia/${saved.slug}`} target="_blank" rel="noopener noreferrer" data-testid="editor-view-live-btn"><ExternalLink /> Ver en la web</a></Button>}
          <Button variant="outline" className="bg-card" onClick={() => save.mutate("draft")} disabled={save.isPending} data-testid="editor-save-draft-btn">Guardar borrador</Button>
          <Button onClick={() => save.mutate(form.status)} disabled={save.isPending} data-testid="editor-publish-btn">{save.isPending && <Loader2 className="animate-spin" />}{primaryLabel}</Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-8 space-y-6">
          <div className="bg-card border border-border px-6 py-5 space-y-3">
            <AutoTextarea value={form.title} onChange={(e) => patch({ title: e.target.value })} placeholder="Titular de la noticia" className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight" data-testid="editor-title-input" />
            <AutoTextarea value={form.summary} onChange={(e) => patch({ summary: e.target.value })} placeholder="Entradilla: en una o dos frases, qué ha pasado y por qué importa" className="font-serif text-xl text-muted-foreground leading-relaxed" data-testid="editor-summary-input" />
          </div>
          <RichEditor value={form.body} onChange={(body) => patch({ body })} />
          <KeyPointsPanel points={form.key_points} onChange={(key_points) => patch({ key_points })} />
          <FaqPanel faqs={form.faqs} onChange={(faqs) => patch({ faqs })} />
        </div>
        <div className="col-span-12 xl:col-span-4 space-y-6">
          <PublishPanel form={form} patch={patch} />
          <Panel title="Sección y etiquetas" testid="editor-category-panel">
            <FieldLabel>Sección</FieldLabel>
            <Select value={form.category_slug} onValueChange={(v) => patch({ category_slug: v })}>
              <SelectTrigger data-testid="editor-category-select"><SelectValue placeholder="Elige sección" /></SelectTrigger>
              <SelectContent>{cats?.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="mt-4"><FieldLabel hint="Enter para añadir">Etiquetas</FieldLabel><TagInput value={form.tags} onChange={(tags) => patch({ tags })} placeholder="banca, pymes, ibex…" testid="editor-tags" /></div>
          </Panel>
          <ImagePanel form={form} patch={patch} />
          <SeoPanel form={form} patch={patch} />
          <AdvancedSeoPanel form={form} patch={patch} />
          {saved?.source?.url && (
            <Panel title="Fuente original" testid="editor-source-panel">
              <a href={saved.source.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 text-sm hover:text-primary">
                <Newspaper className="h-4 w-4 mt-0.5 shrink-0" /><span><strong>{saved.source.name}</strong>{saved.source.title ? ` · ${saved.source.title}` : ""}</span>
              </a>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}
